import Service from '@ember/service';
import { cancel, later } from '@ember/runloop';
import Ember from 'ember';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { EmberRunTimer } from '@ember/runloop/types';
import ENV from 'cio/config/environment';
import { SnackbarLayout, SnackbarState } from 'fly-components/components/fly-snackbar';

export type Snackbar = {
  id: string;
  message: string;
  state?: SnackbarState;
  layout?: SnackbarLayout;
  action?: {
    title: string;
    onClick?: () => unknown;
    link?: {
      route: string;
      query?: Record<string, any>;
    };
  };
  classList: string;
  timer: EmberRunTimer | null;
  duration: number;
  startTs: number;
};

export default class SnackbarService extends Service {
  @tracked current: Snackbar[] = [];

  add(
    id: Snackbar['id'],
    message: Snackbar['message'],
    action?: Snackbar['action'],
    state?: Snackbar['state'],
    layout?: Snackbar['layout'],
  ) {
    let duration = ENV.APP.alertDuration;

    if (action != null) {
      duration = ENV.APP.alertDuration * 2;
    }

    let startTs = new Date().getTime();
    let timer = null;
    let classList = 'transitioning-in';

    if (!Ember.testing) {
      timer = later(() => this.remove(id), duration);
    }

    // Clear existing instances of this snackbar
    this.current = this.current.filter((sb) => sb.id !== id);
    let snackbar: Snackbar = { id, state, message, layout, action, duration, startTs, timer, classList };

    if (!Ember.testing) {
      later(() => {
        this.#updateSnackbar(id, { classList: '' });
      }, 150);
    }

    this.current = [...this.current, snackbar];
  }

  @action
  remove(id: Snackbar['id']) {
    let snackbar = this.current.find((sb) => sb.id === id);

    // Avoid any memory leaks/side-effects and eagerly cancel the timer
    if (snackbar?.timer != null) {
      cancel(snackbar.timer);
    }

    this.#updateSnackbar(id, { classList: 'transitioning-out' });

    if (!Ember.testing) {
      later(() => {
        this.current = this.current.filter((sb) => sb.id !== id);
      }, 150);
    }
  }

  @action
  cancelTimer(id: Snackbar['id']) {
    let snackbar = this.current.find((sb) => sb.id === id);
    let now = new Date().getTime();

    if (snackbar?.timer == null) {
      return;
    }

    let elapsed = now - snackbar.startTs;
    let duration = snackbar.duration - elapsed;

    cancel(snackbar.timer);

    this.#updateSnackbar(id, { duration, timer: null });
  }

  @action
  resumeTimer(id: Snackbar['id']) {
    let snackbar = this.current.find((sb) => sb.id === id);
    let now = new Date().getTime();

    if (snackbar == null || snackbar.timer != null) {
      return;
    }

    let timer: Snackbar['timer'] = null;

    if (!Ember.testing) {
      timer = later(() => this.remove(id), snackbar.duration);
    }

    this.#updateSnackbar(id, { timer, startTs: now });
  }

  clearAlerts() {
    this.current = [];
  }

  #updateSnackbar(id: Snackbar['id'], updates: Partial<Snackbar>) {
    this.current = this.current.map((sb) => {
      // Not this snackbar, move on
      if (sb.id !== id) {
        return sb;
      }

      return {
        ...sb,
        ...updates,
      };
    });
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your services.
declare module '@ember/service' {
  interface Registry {
    snackbar: SnackbarService;
  }
}
